import { Accordion, Alert, Badge, Stack, Table, Text } from '@mantine/core';
import { IconInfoCircle, IconSearch } from '@tabler/icons-react';
import { useGenealogy } from '../api';
import { formatDateTime, formatQty } from '../format';
import { LocationName, ProductName } from './EntityNames';

/** Time-windowed provenance panel on dispatch detail (BUILD_SPEC.md §9.7, the
 * recall-audit affordance): for each dispatch line, which receipts fed the source
 * silo within the window since it was last empty (or the whole ledger history if
 * never empty). Calls `GET /reporting/genealogy?dispatchId=` (services/api/src/
 * modules/reporting/genealogy.ts, G19) — no fake lot-exactness through blending
 * silos, so this is presented as "what likely fed this load", not an exact lot
 * trace. */
export function GenealogyPanel({ dispatchId }: { dispatchId: string }) {
  const { data, isLoading, isError } = useGenealogy(dispatchId);

  if (isLoading) {
    return (
      <Text c="dimmed" size="sm">
        Loading genealogy…
      </Text>
    );
  }
  if (isError || !data) {
    return (
      <Alert color="red" icon={<IconInfoCircle size={18} />}>
        Could not load genealogy for this dispatch.
      </Alert>
    );
  }

  return (
    <Stack gap="sm">
      <Alert icon={<IconSearch size={18} />} color="graphite" variant="light">
        Provenance since the source location was last empty (or its full ledger history if never empty) — this is the
        recall-audit trail for this load.
      </Alert>
      <Accordion multiple defaultValue={data.lines.map((l) => l.lineId)}>
        {data.lines.map((line) => (
          <Accordion.Item key={line.lineId} value={line.lineId}>
            <Accordion.Control>
              <ProductName id={line.productId} /> — from <LocationName id={line.fromLocationId} /> — {formatQty(line.qty)} kg
              {line.windowStart && (
                <Text span c="dimmed" size="sm" ml={8}>
                  (window since {formatDateTime(line.windowStart)})
                </Text>
              )}
            </Accordion.Control>
            <Accordion.Panel>
              {line.feedingReceipts.length === 0 ? (
                <Text c="dimmed" size="sm">
                  No feeding receipts found within the window — this load may have come from opening balance, or the
                  window could not be bounded.
                </Text>
              ) : (
                <Table.ScrollContainer minWidth={640}>
                  <Table verticalSpacing="xs">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Goods receipt</Table.Th>
                        <Table.Th>Received</Table.Th>
                        <Table.Th>Product</Table.Th>
                        <Table.Th>Qty (wet)</Table.Th>
                        <Table.Th>Qty (dry)</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {line.feedingReceipts.map((r) => (
                        <Table.Tr key={r.movementId}>
                          <Table.Td>
                            {r.goodsReceiptNumber ?? <Badge color="gray" variant="light">production</Badge>}
                          </Table.Td>
                          <Table.Td>{formatDateTime(r.occurredAt)}</Table.Td>
                          <Table.Td>
                            <ProductName id={r.productId} />
                          </Table.Td>
                          <Table.Td>{formatQty(r.qty)}</Table.Td>
                          <Table.Td>{formatQty(r.dryKg)}</Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              )}
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>
    </Stack>
  );
}
